import React, { useState, useEffect } from 'react';
import { DndContext, DragEndEvent, DragOverlay, useDraggable, useDroppable, DragStartEvent } from "@dnd-kit/core";
import { 
  Button, Dialog, DialogActions, DialogContent, DialogTitle, 
  MenuItem, Table, TableBody, TableCell, TableHead, 
  TableRow, Menu, IconButton
} from '@mui/material';
import { MoreHorizontal, Phone, Search, CheckCircle, AlertTriangle, GripVertical, FileCheck, X, ExternalLink, Minus, AlertCircle } from 'lucide-react';
import { MultiDialerQuotaInfo } from "interfaces/subscriptions";
import { UserDataResult } from "interfaces/users";
import { updateVisibleAccounts } from 'lib/redux/users/slice';
import { useDispatch } from 'react-redux';
import { useUserData } from '../../lib/redux/users/user-data-hook';
import { AgentType, MultiDialerSubscription, OptionalTwilioNumber, RegisteredTwilioNumber } from 'interfaces/db';
import { getServicesManager } from 'services';
import { format_number } from './cfg';
import { NumberSearch } from './NumberSearch';
import { usePurchasing } from './hooks';
import { Typography } from 'interfaces/typography';
import { formatTimeAgo } from './utils';
import { useNavigate } from 'react-router-dom';

interface DraggableNumberProps {
  numberData: RegisteredTwilioNumber;
  onRelease: () => void;
}

function DraggableNumber({ numberData, onRelease }: DraggableNumberProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: numberData.rep_phone_value
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  const spamStatus = getSpamStatus(numberData);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRelease = () => {
    onRelease();
    handleClose();
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  };

  const handleTouchStart = (event: React.TouchEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  };

  return (
    <div
      ref={setNodeRef}
      className={`flex items-center p-3 mb-2 rounded-lg border border-gray-200 bg-white ${
        isDragging ? "opacity-50 cursor-grabbing" : "cursor-pointer"
      }`}
    >
      <div className="flex items-center flex-1">
        <GripVertical
          {...listeners}
          {...attributes}
          className="mr-2 text-gray-500 cursor-grab"
          size={16}
          aria-label="Drag Handle"
        />
        <Typography variant="smallParagraph" color="#000000" fontWeight={400} fontSize="12px">{numberData.rep_phone_value}</Typography>
      </div>
      <div className="flex items-center gap-2">
        {numberData.free_caller_registry_status === 'SUBMITTED' && (
          <div className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 ${
            numberData.free_caller_registry_status_as_of && new Date().getTime() - new Date(numberData.free_caller_registry_status_as_of).getTime() < 7 * 24 * 60 * 60 * 1000 
              ? 'bg-amber-100 text-amber-700' 
              : 'bg-emerald-100 text-emerald-700'
          }`}>
            <FileCheck size={8} />
            <Typography variant="smallCaption" fontSize="10px" fontWeight={500}>
              {numberData.free_caller_registry_status_as_of && new Date().getTime() - new Date(numberData.free_caller_registry_status_as_of).getTime() < 7 * 24 * 60 * 60 * 1000 
                ? 'Pending' 
                : 'Registry'}
            </Typography>
          </div>
        )}
        {spamStatus === 'flagged' ? (
          <AlertTriangle className="text-red-500" size={16} aria-label="High Spam Score" />
        ) : spamStatus === 'warning' ? (
          <AlertCircle className="text-yellow-500" size={16} aria-label="Warning Spam Score" />
        ) : spamStatus === 'clean' ? (
          <CheckCircle className="text-green-500" size={16} aria-label="Low Spam Score" />
        ) : (
          <Minus className="text-gray-400" size={16} aria-label="Not Tested" />
        )}
      </div>
      <IconButton
        size="small"
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        aria-label="More options"
      >
        <MoreHorizontal className="h-4 w-4" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disableAutoFocusItem
      >
        <MenuItem onClick={handleRelease}>Release Number</MenuItem>
      </Menu>
    </div>
  );
}

interface DroppableZoneProps {
  rep: UserDataResult;
  numbers: RegisteredTwilioNumber[];
  subscription?: MultiDialerSubscription;
  onReleaseNumber: (number: string) => void;
  openModal: () => void;
  onUpdateForwarding: (userId: string, number: string | null) => Promise<boolean>;
  onRefreshSpamTest: (userId: string, updatedNumbers: RegisteredTwilioNumber[]) => void;
}

function DroppableZone({ rep, numbers, onReleaseNumber, subscription, openModal, onUpdateForwarding, onRefreshSpamTest }: DroppableZoneProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: rep.user_id.toString(),
  });
  
  const [isEditingForwarding, setIsEditingForwarding] = useState(false);
  const [forwardingValue, setForwardingValue] = useState(rep.forwarding_number || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshingSpam, setIsRefreshingSpam] = useState(false);

  const isAtLimit = subscription 
    ? numbers.length >= subscription.max_phone_numbers_per_user 
    : false;

  const isButtonDisabled = !rep.parallel_enabled || !rep.can_dial || isAtLimit;
  
  const lastSpamTest = getLatestSpamTestDate(numbers);
  const canRefreshSpam = lastSpamTest ? (Date.now() - lastSpamTest.getTime()) > (3 * 24 * 60 * 60 * 1000) : true;

  const handleForwardingEdit = () => {
    setForwardingValue(rep.forwarding_number || '');
    setIsEditingForwarding(true);
  };

  const handleForwardingCancel = () => {
    setForwardingValue(rep.forwarding_number || '');
    setIsEditingForwarding(false);
  };

  const handleForwardingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setForwardingValue(value);
  };

  const handleForwardingBlur = () => {
    let value = forwardingValue.trim();
    if (value && !value.startsWith('+')) {
      value = formatPhoneNumber(value);
      setForwardingValue(value);
    }
  };

  const handleForwardingSave = async () => {
    setIsSaving(true);
    let trimmedValue = forwardingValue.trim();
    
    // Format the number if it doesn't start with + before saving
    if (trimmedValue && !trimmedValue.startsWith('+')) {
      trimmedValue = formatPhoneNumber(trimmedValue);
      setForwardingValue(trimmedValue);
    }
    
    const numberToSave = trimmedValue || null;
    
    try {
      const success = await onUpdateForwarding(rep.user_id, numberToSave);
      if (success) {
        setIsEditingForwarding(false);
        // Update the rep object locally would require parent state management
        // For now, we'll just exit edit mode and let the parent handle the refresh
      }
    } catch (error) {
      console.error('Failed to save forwarding number:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const formatPhoneNumber = (value: string): string => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length === 0) return '';
    if (cleaned.length <= 10) {
      return `+1${cleaned}`;
    }
    return `+${cleaned}`;
  };

  const handleRefreshSpamTest = async () => {
    if (!canRefreshSpam || isRefreshingSpam) return;
    
    setIsRefreshingSpam(true);
    try {
      // Call the new service function to refresh spam tests
      const updatedNumbers = await getServicesManager().getTwilioNumbersForUser(rep.user_id, true);
      if (updatedNumbers) {
        // Filter to only numbers owned by this user
        const userNumbers = updatedNumbers.filter(num => num.agent_id === rep.user_id);
        onRefreshSpamTest(rep.user_id, userNumbers);
      }
    } catch (error) {
      console.error('Failed to refresh spam test:', error);
    } finally {
      setIsRefreshingSpam(false);
    }
  };

  return (
    <div 
      ref={setNodeRef} 
      className={`p-4 rounded-lg border border-gray-200 ${
        isOver ? 'bg-blue-100 border-blue-300' : 'bg-white'
      }`}
    >
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <div>
            <Typography variant="h3" fontWeight={600}>
              {rep.user_name}
            </Typography>
            {lastSpamTest && (
              <div className="flex items-center gap-2">
                <Typography variant="smallParagraph" fontSize="11px" color="#6b7280" fontWeight={400}>
                  Last spam test: {formatTimeAgo(lastSpamTest)}
                </Typography>
                {canRefreshSpam && (
                  <button
                    onClick={handleRefreshSpamTest}
                    disabled={isRefreshingSpam}
                    className="text-blue-600 hover:text-blue-800 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Refresh spam test (available every 3 days)"
                  >
                    {isRefreshingSpam ? 'Refreshing...' : 'Refresh'}
                  </button>
                )}
              </div>
            )}
          </div>
          <Button
            onClick={openModal}
            variant="outlined"
            disabled={isButtonDisabled}
            startIcon={<Phone />}
            style={{
              borderColor: isButtonDisabled ? '#ccc' : '#000',
              color: isButtonDisabled ? '#777' : '#000',
              textTransform: 'none',
              borderRadius: '6px',
              padding: '4px 10px',
              fontSize: '0.875rem',
            }}
            className="transition-colors duration-200 hover:bg-gray-100"
          >
            <Typography variant="largeParagraph" color="#000000" fontWeight={400} fontSize="12px">{`${numbers.length}/${subscription?.max_phone_numbers_per_user || '—'} Assigned`}</Typography>
          </Button>
        </div>
        
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 rounded-lg border border-gray-200">
          {isEditingForwarding ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                  <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                </div>
                <Typography variant="smallParagraph" fontSize="12px" color="#374151" fontWeight={600}>
                  Edit forwarding number
                </Typography>
              </div>
              <div className="space-y-2">
                <input
                  type="text"
                  value={forwardingValue}
                  onChange={handleForwardingChange}
                  onBlur={handleForwardingBlur}
                  placeholder="Enter phone number"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  disabled={isSaving}
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleForwardingSave}
                    disabled={isSaving}
                    className="flex-1 px-3 py-2 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 font-medium flex items-center justify-center gap-1"
                  >
                    {isSaving ? (
                      <>
                        <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Saving</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Save
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleForwardingCancel}
                    disabled={isSaving}
                    className="flex-1 px-3 py-2 text-xs bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 font-medium flex items-center justify-center gap-1"
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div 
              onClick={handleForwardingEdit}
              className="group cursor-pointer flex items-center gap-3 hover:bg-white hover:shadow-sm rounded-lg p-1"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                {rep.forwarding_number ? (
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-gray-400 group-hover:text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                {rep.forwarding_number ? (
                  <div className="flex items-center gap-1">
                    <Typography variant="smallParagraph" fontSize="12px" color="#6b7280" fontWeight={500}>
                      Forwarding:
                    </Typography>
                    <Typography variant="smallParagraph" fontSize="12px" color="#374151" fontWeight={500}>
                      {rep.forwarding_number}
                    </Typography>
                  </div>
                ) : (
                  <Typography variant="smallParagraph" fontSize="12px" color="#9ca3af" fontWeight={400}>
                    Click to set forwarding number
                  </Typography>
                )}
              </div>
              {rep.forwarding_number && (
                <div className="text-gray-400 group-hover:text-gray-600">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="space-y-2">
        {numbers.map((number) => (
          <DraggableNumber 
            key={number.rep_phone_value} 
            numberData={number}
            onRelease={() => onReleaseNumber(number.rep_phone_value)}
          />
        ))}
      </div>
    </div>
  );
}

export function NumberManagementAdmin() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { allParallelEnabledUsers: parallel_users, allUsers: all_users, visibleAccounts } = useUserData();
  const [selectedNumbers, setSelectedNumbers] = useState<RegisteredTwilioNumber[]>([]);
  const [quotaInfo, setQuotaInfo] = useState<MultiDialerQuotaInfo | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedRep, setSelectedRep] = useState<UserDataResult | null>(null);

  const { purchasingNumbers, purchaseNumber } = usePurchasing(selectedNumbers, setSelectedNumbers);

  function handleDragStart(event: DragStartEvent) {
    setDraggedId(event.active.id as string);
  }

  useEffect(() => {
    getServicesManager().getAllTwilioNumbersAdmin().then((numbers) => {
      if (!numbers) return;
      setSelectedNumbers(numbers);
    });

    getServicesManager().getMultidialerQuota().then((quota) => {
      if (!quota) return;
      setQuotaInfo(quota);
    });
  }, []);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setDraggedId(null);
    if (!over) return;
    if (!active) return;
    const matchingNumber = selectedNumbers.find((n) => n.rep_phone_value.toString() === active.id);
    if (!matchingNumber) return;
    if (numberIsOwnedByUserId(matchingNumber, over.id.toString())) return
    // check number of numbers for that user....
    if (!quotaInfo) return
    if (selectedNumbers.filter(n => numberIsOwnedByUserId(n, over.id.toString())).length >= quotaInfo?.subscription.max_phone_numbers_per_user) return
    setSelectedNumbers((prev) => {
      const updated = [...prev];
      const activeIndex = updated.findIndex((n) => n.rep_phone_value.toString() === active.id);
      updated[activeIndex] = { ...updated[activeIndex], agent_type: AgentType.USER, agent_id: over.id.toString() };
      return updated
    })
    reassignNumber(matchingNumber, over.id.toString());
  }

  const reassignNumber = async (number: RegisteredTwilioNumber, newUserId: string) => {
    await getServicesManager().reassignTwilioNumberAdmin(number.rep_phone_value, newUserId);
  };

  const handlePurchaseNumber = async (number: OptionalTwilioNumber, countryCode: string, numberType: 'Local' | 'Mobile' | 'TollFree') => {
    if (!selectedRep || !selectedRep.parallel_enabled || !selectedRep.can_dial || !selectedRep.team_is_active) return;
    if (!quotaInfo) return;
    const numbers_set = selectedNumbers.filter(n => numberIsOwnedByUserId(n, selectedRep.user_id))
    if (numbers_set.length >= quotaInfo.subscription.max_phone_numbers_per_user) {
      alert(`You can only select up to ${quotaInfo.subscription.max_phone_numbers_per_user} numbers.`);
      return;
    }
    await purchaseNumber(number, selectedRep, countryCode, numberType, true);
  };

  const handleReleaseNumber = async (phone_number: string) => {
    setSelectedNumbers((prev) => prev.filter((n) => n.rep_phone_value !== phone_number));
    await getServicesManager().releaseTwilioNumberAdmin(phone_number);
  };

  const openModal = (rep: UserDataResult) => {
    setSelectedRep(rep);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedRep(null);
    setIsModalOpen(false);
  };

  const handleUpdateForwardingNumberForUser = async (userId: string, number: string | null): Promise<boolean> => {
    try {
      const result = await getServicesManager().updateForwardingNumberForUser(number, userId);
      if (result !== null && visibleAccounts) {
        // Update the visibleAccounts Redux state
        const updatedUsers = visibleAccounts.users.map((user: any) => {
          if (user.user_id === userId) {
            return { ...user, forwarding_number: number };
          }
          return user;
        });
        
        dispatch(updateVisibleAccounts({
          teams: visibleAccounts.teams,
          users: updatedUsers
        }));
      }
      return result !== null;
    } catch (error) {
      console.error('Failed to update forwarding number:', error);
      return false;
    }
  };

  const draggedNumber = draggedId 
    ? selectedNumbers.find(n => n.rep_phone_value.toString() === draggedId)
    : null;

  const allReps: UserDataResult[] = Array.from(new Set([
    ...selectedNumbers.filter(v => v.agent_type === AgentType.USER).map(v => v.agent_id),
    ...parallel_users.map(v => v.user_id),
  ])).map((v) => all_users.find((u) => u.user_id === v)).filter((v): v is UserDataResult => v !== undefined).sort((a, b) => a.user_name.localeCompare(b.user_name))

  // Filter reps based on search query
  const filteredReps = allReps.filter(rep => 
    rep.user_name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleClearSearch = () => {
    setSearchQuery('')
  }

  const handleRefreshSpamTest = (userId: string, updatedNumbers: RegisteredTwilioNumber[]) => {
    // Update the selectedNumbers state by replacing numbers for the specific user
    setSelectedNumbers(prev => {
      const otherNumbers = prev.filter(num => !(num.agent_id === userId && num.agent_type === AgentType.USER));
      return [...otherNumbers, ...updatedNumbers];
    });
  }

  return (
    <div className='flex flex-col gap-6 p-4'>
      {/* Search and Filter Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <Typography variant="h3" fontWeight={600} color="#1f2937">
            Number Management
          </Typography>
          <Typography variant="smallParagraph" color="#6b7280" fontWeight={400}>
            {filteredReps.length} of {allReps.length} users shown
          </Typography>
        </div>
        
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search users by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm placeholder-gray-500 transition-colors duration-200"
          />
          {searchQuery && (
            <button
              onClick={handleClearSearch}
              className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-gray-600 text-gray-400 transition-colors duration-200"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        
        {searchQuery && (
          <div className="mt-3 flex items-center gap-2">
            <Typography variant="smallParagraph" color="#6b7280" fontWeight={400}>
              Searching for:
            </Typography>
            <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-md border border-blue-200">
              <Typography variant="smallParagraph" fontWeight={500} fontSize="12px">
                "{searchQuery}"
              </Typography>
              <button
                onClick={handleClearSearch}
                className="hover:text-blue-900 transition-colors duration-200"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}
        
        {/* Spam Status Guide */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Typography variant="smallParagraph" fontSize="11px" color="#374151" fontWeight={600}>
                Spam Status:
              </Typography>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  <CheckCircle className="text-green-500" size={14} />
                  <Typography variant="smallParagraph" fontSize="10px" color="#374151" fontWeight={400}>Clean</Typography>
                </div>
                <div className="flex items-center gap-1">
                  <AlertCircle className="text-yellow-500" size={14} />
                  <Typography variant="smallParagraph" fontSize="10px" color="#374151" fontWeight={400}>Warning</Typography>
                </div>
                <div className="flex items-center gap-1">
                  <AlertTriangle className="text-red-500" size={14} />
                  <Typography variant="smallParagraph" fontSize="10px" color="#374151" fontWeight={400}>Flagged</Typography>
                </div>
                <div className="flex items-center gap-1">
                  <Minus className="text-gray-400" size={14} />
                  <Typography variant="smallParagraph" fontSize="10px" color="#374151" fontWeight={400}>Untested</Typography>
                </div>
              </div>
              <Typography variant="smallParagraph" fontSize="10px" color="#6b7280" fontWeight={400}>
                Tests run ~weekly via Nomorobo & IPQualityScore
              </Typography>
            </div>
            <button
              onClick={() => navigate('/settings?tab=Registrations')}
              className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors shadow-sm"
            >
              <ExternalLink size={13} />
              <Typography variant="smallParagraph" color="#ffffff" fontSize="12px" fontWeight={500}>Reduce spam risk</Typography>
            </button>
          </div>
        </div>
      </div>

      <DndContext
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {filteredReps.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center shadow-sm">
            <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <Typography variant="h3" color="#6b7280" fontWeight={500} className="mb-2">
              No users found
            </Typography>
            <Typography variant="smallParagraph" color="#9ca3af">
              {searchQuery ? `No users match "${searchQuery}"` : 'No users available'}
            </Typography>
            {searchQuery && (
              <button
                onClick={handleClearSearch}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                <Typography variant="smallParagraph" color="#ffffff" fontWeight={500}>
                  Clear search
                </Typography>
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredReps.map((rep) => (
              <DroppableZone
                key={rep.user_id}
                rep={rep}
                numbers={selectedNumbers.filter(num => numberIsOwnedByUserId(num, rep.user_id))}
                onReleaseNumber={(phone_number) => handleReleaseNumber(phone_number)}
                subscription={quotaInfo?.subscription}
                openModal={() => openModal(rep)}
                onUpdateForwarding={handleUpdateForwardingNumberForUser}
                onRefreshSpamTest={handleRefreshSpamTest}
              />
            ))}
          </div>
        )}

        <DragOverlay>
          {draggedId && draggedNumber && (() => {
            const draggedSpamStatus = getSpamStatus(draggedNumber);
            return (
              <div className="p-2 rounded-lg border border-gray-200 bg-white flex flex-col">
                <div className="flex items-center gap-2">
                  <Typography variant="smallParagraph" color="#000000" fontWeight={400} fontSize="12px">{format_number(draggedNumber.rep_phone_value)}</Typography>
                  {draggedNumber.free_caller_registry_status === 'SUBMITTED' && (
                    <div className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 ${
                      draggedNumber.free_caller_registry_status_as_of && new Date().getTime() - new Date(draggedNumber.free_caller_registry_status_as_of).getTime() < 7 * 24 * 60 * 60 * 1000 
                        ? 'bg-amber-100 text-amber-700' 
                        : 'bg-emerald-100 text-emerald-700'
                    }`}>
                      <FileCheck size={8} />
                      <Typography variant="smallCaption" fontSize="10px" fontWeight={500}>
                        {draggedNumber.free_caller_registry_status_as_of && new Date().getTime() - new Date(draggedNumber.free_caller_registry_status_as_of).getTime() < 7 * 24 * 60 * 60 * 1000 
                          ? 'Pending' 
                          : 'Registry'}
                      </Typography>
                    </div>
                  )}
                  {draggedSpamStatus === 'flagged' ? (
                    <AlertTriangle className="text-yellow-500" size={16} aria-label="High Spam Score" />
                  ) : draggedSpamStatus === 'clean' ? (
                    <CheckCircle className="text-green-500" size={16} aria-label="Low Spam Score" />
                  ) : (
                    <Minus className="text-gray-400" size={16} aria-label="Not Tested" />
                  )}
                </div>
              </div>
            );
          })()}
        </DragOverlay>
      </DndContext>

      {selectedRep && (
        <Dialog open={isModalOpen} onClose={closeModal} fullWidth maxWidth="md">
          <DialogTitle>
            Acquire New Number for <strong>{selectedRep.user_name}</strong>
          </DialogTitle>
          <DialogContent dividers>
            <div className="mb-6">
              <Typography variant="h3" fontWeight={600} className="mb-4">
                Current Assigned Numbers
              </Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Phone Number</TableCell>
                    <TableCell>Spam Score</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedNumbers.filter(num => numberIsOwnedByUserId(num, selectedRep.user_id)).map((number) => (
                    <TableRow key={number.rep_phone_value}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {number.rep_phone_value}
                          {number.free_caller_registry_status === 'SUBMITTED' && (
                            <div className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 ${
                              number.free_caller_registry_status_as_of && new Date().getTime() - new Date(number.free_caller_registry_status_as_of).getTime() < 7 * 24 * 60 * 60 * 1000 
                                ? 'bg-amber-100 text-amber-700' 
                                : 'bg-emerald-100 text-emerald-700'
                            }`}>
                              <FileCheck size={8} />
                              <Typography variant="smallCaption" fontSize="10px" fontWeight={500}>
                                {number.free_caller_registry_status_as_of && new Date().getTime() - new Date(number.free_caller_registry_status_as_of).getTime() < 7 * 24 * 60 * 60 * 1000 
                                  ? 'Pending' 
                                  : 'Registry'}
                              </Typography>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {(() => {
                            const status = getSpamStatus(number);
                            return status === 'flagged' ? (
                              <AlertTriangle className="text-red-500" size={16} aria-label="High Spam Score" />
                            ) : status === 'warning' ? (
                              <AlertCircle className="text-yellow-500" size={16} aria-label="Warning Spam Score" />
                            ) : status === 'clean' ? (
                              <CheckCircle className="text-green-500" size={16} aria-label="Low Spam Score" />
                            ) : (
                              <Minus className="text-gray-400" size={16} aria-label="Not Tested" />
                            );
                          })()}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div>
              <Typography variant="h3" fontWeight={600} className="mb-4">
                Acquire New Number
              </Typography>
              <NumberSearch 
                disabled={!selectedRep.parallel_enabled || !selectedRep.can_dial}
                handlePurchase={(number, code, numberType) => {
                  handlePurchaseNumber(number, code, numberType)
                }}
                purchasingNumbers={purchasingNumbers}
                quota={quotaInfo}
                registeredNumbers={selectedNumbers}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeModal} color="primary" variant="text">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
}


function numberIsOwnedByUserId(n: RegisteredTwilioNumber, user_id: string): boolean {
  return n.agent_type === AgentType.USER && n.agent_id === user_id
}

function getLatestSpamTestDate(numbers: RegisteredTwilioNumber[]): Date | null {
  const dates: Date[] = [];
  
  numbers.forEach(number => {
    if (number.nomorobo_spamscore_as_of) {
      dates.push(new Date(number.nomorobo_spamscore_as_of));
    }
    if (number.ipqualityscore_as_of) {
      dates.push(new Date(number.ipqualityscore_as_of));
    }
  });
  
  if (dates.length === 0) return null;
  
  return new Date(Math.max(...dates.map(date => date.getTime())));
}

function getSpamStatus(number: RegisteredTwilioNumber): 'clean' | 'flagged' | 'warning' | 'untested' {
  // Check if we have any test results at all
  const hasNomoroboResult = number.nomorobo_spamscore !== null;
  const hasIpQualityResult = number.ipqualityscore_score !== null;
  
  // If we have no results from either provider, show untested
  if (!hasNomoroboResult && !hasIpQualityResult) {
    return 'untested';
  }
  
  // Check if either provider flagged spam
  const nomoroboFlagged = hasNomoroboResult && number.nomorobo_spamscore && number.nomorobo_spamscore > 0.5;
  const ipQualityScoreFlagged = hasIpQualityResult && number.ipqualityscore_score !== null && number.ipqualityscore_score > 85;
  
  // If either provider shows spam, flag it
  if (nomoroboFlagged || ipQualityScoreFlagged) {
    return 'flagged';
  }
  
  // Check for warning status: Nomorobo clean but IPQuality 75-85
  const nomoroboClean = hasNomoroboResult && (!number.nomorobo_spamscore || number.nomorobo_spamscore <= 0.5);
  const ipQualityWarning = hasIpQualityResult && number.ipqualityscore_score !== null && number.ipqualityscore_score > 75 && number.ipqualityscore_score <= 85;
  
  if (nomoroboClean && ipQualityWarning) {
    return 'warning';
  }
  
  // If we have results but no spam detected, show clean
  return 'clean';
}
